import React, { useState } from 'react';
import { CheckCircle2, Info, Loader2, PlugZap, Send, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Button } from '@/components/ui/button';
import { useCheckConnection } from '@/api/integrations/use-integrations';
import type {
    ConnectionCheckResult,
    TenantIntegration,
} from '@/api/integrations/integrationService';
import type { IntegrationChannel } from './catalog';
import { formatRelativeTime } from './integrationHelpers';
import { SendTestDialog } from './SendTestDialog';

interface VerifySectionProps {
    channel: IntegrationChannel;
    providerName: string;
    /** The saved row; checks and tests run against what is stored. */
    row?: TenantIntegration;
    /** The form has unsaved edits: a check would still read the old credentials. */
    dirty: boolean;
    canCheck: boolean;
    /** The provider has no credentials-only check (backend answers `supported: false`). */
    checkSupported?: boolean;
    /** Really sending needs edit rights and a channel that can send. */
    canSend: boolean;
    /**
     * The backend sends through the channel's ACTIVE provider whichever row is tested, so
     * "Send a test" is only offered for the provider that is on (and saved).
     */
    isActive: boolean;
}

/** One persisted outcome line: "Last check: 2 hours ago · OK". */
const OutcomeLine: React.FC<{
    label: string;
    at?: string | null;
    ok?: boolean | null;
    message?: string | null;
}> = ({ label, at, ok, message }) => {
    const { t, i18n } = useTranslation('crm');
    const when = formatRelativeTime(at, i18n.language);
    if (!when || ok === null || ok === undefined) return null;

    return (
        <p
            className={`flex items-start gap-1.5 text-xs ${ok ? 'text-muted-foreground' : 'text-destructive'}`}
        >
            {ok ? (
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
            ) : (
                <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            )}
            <span>
                {label}: {when} ·{' '}
                {ok ? t('integrations.verify.ok') : message || t('integrations.verify.failed')}
            </span>
        </p>
    );
};

/**
 * "Check connection" verifies the saved credentials and sends nothing.
 * "Send a test …" really sends and says so. Both run on the saved row, so unsaved
 * edits disable them until the form is saved.
 */
export const VerifySection: React.FC<VerifySectionProps> = ({
    channel,
    providerName,
    row,
    dirty,
    canCheck,
    checkSupported = true,
    canSend,
    isActive,
}) => {
    const { t } = useTranslation('crm');
    const { enqueueSnackbar } = useSnackbar();
    const check = useCheckConnection();
    const [unsupported, setUnsupported] = useState<string | undefined>();
    const [lastResult, setLastResult] = useState<ConnectionCheckResult | undefined>();
    const [testOpen, setTestOpen] = useState(false);

    const blockedReason = !row
        ? t('integrations.verify.saveFirst')
        : dirty
          ? t('integrations.verify.unsaved')
          : undefined;
    const sendBlockedReason =
        blockedReason ?? (isActive ? undefined : t('integrations.verify.sendNeedsActive'));

    const runCheck = async () => {
        if (!row) return;
        setUnsupported(undefined);
        setLastResult(undefined);
        try {
            const result = await check.mutateAsync(row.id);
            setLastResult(result);
            if (result.supported === false) {
                setUnsupported(result.message || t('integrations.verify.unsupported'));
            } else if (result.valid) {
                enqueueSnackbar(result.message || t('integrations.verify.checkOk'), {
                    variant: 'success',
                });
            } else {
                enqueueSnackbar(result.message || t('integrations.verify.checkFailed'), {
                    variant: 'error',
                });
            }
        } catch (error: any) {
            enqueueSnackbar(
                error?.response?.data?.message || t('integrations.verify.checkFailed'),
                {
                    variant: 'error',
                }
            );
        }
    };

    if (!canCheck && !canSend) return null;

    return (
        <section className="space-y-4" aria-labelledby="verify-heading">
            <h3 id="verify-heading" className="text-sm font-semibold text-foreground">
                {t('integrations.sections.verify')}
            </h3>

            {canCheck && (
                <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-border p-3">
                    <div className="min-w-0 flex-1 space-y-1">
                        <p className="text-sm font-medium">{t('integrations.verify.check')}</p>
                        <p className="text-xs text-muted-foreground">
                            {t('integrations.verify.checkHint')}
                        </p>
                        {!checkSupported ? (
                            <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                {t('integrations.verify.unsupported')}
                            </p>
                        ) : unsupported ? (
                            <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                {unsupported}
                            </p>
                        ) : lastResult && lastResult.valid === false ? (
                            <p className="flex items-start gap-1.5 text-xs text-destructive">
                                <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                {lastResult.message || t('integrations.verify.checkFailed')}
                            </p>
                        ) : (
                            <OutcomeLine
                                label={t('integrations.verify.lastCheck')}
                                at={row?.last_checked_at}
                                ok={row?.last_check_ok}
                                message={row?.last_check_message}
                            />
                        )}
                        {checkSupported && blockedReason && (
                            <p className="text-xs text-muted-foreground">{blockedReason}</p>
                        )}
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={runCheck}
                        disabled={!checkSupported || !!blockedReason || check.isPending}
                    >
                        {check.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <PlugZap className="h-4 w-4" />
                        )}
                        {t('integrations.verify.checkAction')}
                    </Button>
                </div>
            )}

            {canSend && (
                <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-border p-3">
                    <div className="min-w-0 flex-1 space-y-1">
                        <p className="text-sm font-medium">
                            {t(`integrations.verify.send.${channel}`)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                            {t('integrations.verify.sendHint')}
                        </p>
                        <OutcomeLine
                            label={t('integrations.verify.lastTest')}
                            at={row?.last_tested_at}
                            ok={row?.last_test_ok}
                            message={row?.last_test_message}
                        />
                        {sendBlockedReason && (
                            <p className="text-xs text-muted-foreground">{sendBlockedReason}</p>
                        )}
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setTestOpen(true)}
                        disabled={!!sendBlockedReason}
                    >
                        <Send className="h-4 w-4" />
                        {t(`integrations.verify.sendAction.${channel}`)}
                    </Button>
                    {row && (
                        <SendTestDialog
                            open={testOpen}
                            onOpenChange={setTestOpen}
                            row={row}
                            channel={channel}
                            providerName={providerName}
                        />
                    )}
                </div>
            )}
        </section>
    );
};

export default VerifySection;
